import { constants } from '@config';
import { logError } from 'src/utils/logger';

import { InvalidSvgError } from './page-content.errors';

export class PageContentFactory {
  log = logError(this.constructor.name);

  getFonts (svgElement) {
    return [...svgElement.querySelectorAll('[font-family]')]
      .map(text => {
        const family = text.getAttribute('font-family').replace(/['"]/g, '');
        const style = text.getAttribute('font-style') || 'normal';
        const weight = this.#getNumericalFontWeight(text.getAttribute('font-weight') || '400');
        const stretch = text.getAttribute('font-stretch') || 'normal';

        const customFontId = text.getAttribute('custom-font-id') || null;

        return { family, style, weight, customFontId, stretch };
      })
      .reduce((acc, font) => {
        const existingFont = acc.find(f => f.family === font.family);
        const variant = {
          style: font.style,
          weight: font.weight,
          stretch: font.stretch
        };

        if (existingFont) {
          existingFont.variants.push(variant);
        } else {
          acc.push({ ...font, variants: [variant] });
        }

        return acc;
      }, []);
  }

  getImages (svgElement) {
    const images = [...svgElement.querySelectorAll('image')];

    return images;
  }

  async getActivePageSvgString () {
    const activePageId = INKIVE.PageFactory.getActivePageId();
    const svgString = await this.getSvgString(activePageId);

    return svgString;
  }

  async getActivePageSvgElement () {
    const activePageId = INKIVE.PageFactory.getActivePageId();
    const svgElement = await this.getSvgElement(activePageId);
    return svgElement;
  }

  async getSvgString (pageId) {
    const content = await this.#getPageContent(pageId);
    const string = this.sanitizeSvgString(content);
    return string;
  }

  async getSvgElement (pageId) {
    const content = await this.#getPageContent(pageId);
    const element = this.createSvgElement(content);
    return element;
  }

  getStringFromElement (svgElement) {
    // TODO: Do we need svgToString?
    // const svgString = INKIVE.DesignFacade.canvas.svgToString(svgElement);

    // TODO: support textmock
    const svgString = svgElement.outerHTML;
    const sanitizedSvgString = this.sanitizeSvgString(svgString);
    return sanitizedSvgString;
  }

  async #getPageContent (pageId) {
    const activePageId = INKIVE.PageFactory.getActivePageId();

    if (String(pageId) === activePageId) {
      return this.#getActivePageContent();
    }

    const page = await INKIVE.PageFactory.getPage(pageId);
    return page.content;
  }

  #cloneSvg (svgNode) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgNode.outerHTML, 'image/svg+xml');
    return doc.querySelector('svg');
  }

  #getActivePageContent (options) {
    // TODO: Do we need getSvgString?
    const svgElement = document.getElementById('svgcontent');
    return svgElement.outerHTML;

    // TODO: add support for textmock
    // const textMockElement = document.getElementById('textmock');

    // const wrapperSvgElement = `<svg
    // xmlns="http://www.w3.org/2000/svg"
    // xmlns:xlink="http://www.w3.org/1999/xlink"
    // width="${width}"
    // height="${height}"
    // viewBox="0 0 ${width} ${height}">${svgElement.outerHTML}${textMockElement.outerHTML}</svg>`;
  }

  createSvgElement (svgString) {
    const sanitizedSvgString = this.sanitizeSvgString(svgString);
    const parser = new DOMParser();
    const document = parser.parseFromString(sanitizedSvgString, 'text/html');
    const svgElement = document.body.firstElementChild;
    this.#restoreWhitespace(svgElement);
    this.#removeInvalidElements(svgElement);

    if (svgElement instanceof SVGSVGElement) return svgElement;

    throw new InvalidSvgError();
  }

  #removeInvalidElements (svgElement) {
    const invalidElements = [...svgElement.querySelectorAll('#canvasBackground, .svgLoader, [data-sanitize="ignore"]')];
    invalidElements.forEach(el => el.remove());
  }

  sanitizeSvgString (svgString) {
    if (!(svgString && (typeof svgString === 'string' || svgString instanceof String))) {
      return svgString; // Not a string so return;
    }
    // Prevent &nbsp; elements from getting added to the DOM
    return svgString
      .replace(/>[\t\n\s]+<(?!\/(?:tspan|text))/g, '><')
      .replace(/<\/a>[\t\n\s]+<(\/tspan)/g, '</a></tspan')
      .replace(/<\/tspan>\s*/g, '</tspan>')
      .replace(/\s*<tspan/g, '<tspan')
      .replace(/<\/text>\s*/g, '</text>')
      .replace(/\s*<text/g, '<text')
      .replace(/<\/g>\s*/g, '</g>')
      .replace(/<\/svg>\s*/g, '</svg>')
      .replace(/font-family="([^"]+)"/g, (match, fontFamily) => {
        // DOMParser can't handle quotes in font-family names
        return `font-family="${fontFamily.replace(/'/g, '')}"`;
      });
  }

  #restoreWhitespace (svgElement) {
    // Restore whitespace in blank text elements
    const blankTexts = [...svgElement?.querySelectorAll('text.blank')];
    blankTexts.forEach(text => {
      const tspan = text.querySelector('tspan:not(.ordered):not(.bullets)');
      if (tspan) {
        if (tspan.innerHTML !== constants.text.noBreakSpace) {
          tspan.innerHTML = constants.text.noBreakSpace;
        }
      } else if (text.innerHTML !== constants.text.noBreakSpace) {
        text.innerHTML = constants.text.noBreakSpace;
      }
    });
  }

  getSvgElementDimensions (svgElement) {
    const width = Number(svgElement.getAttribute('width'));
    const height = Number(svgElement.getAttribute('height'));

    return [width, height];
  }

  setSvgElementDimensions (svgElement, options) {
    const [unscaledWidth, unscaledHeight] =
      this.getSvgElementDimensions(svgElement);

    const width = options?.width ?? unscaledWidth;
    const height = options?.height ?? unscaledHeight;
    const x = options?.x ?? 0;
    const y = options?.y ?? 0;
    const viewBox = options?.viewBox ?? `${x} ${y} ${unscaledWidth} ${unscaledHeight}`;

    svgElement.setAttribute('viewBox', viewBox);
    svgElement.setAttribute('width', width);
    svgElement.setAttribute('height', height);

    return svgElement;
  }

  #getNumericalFontWeight (fontWeight) {
    const map = {
      normal: '400',
      bold: '700'
    };

    return map[fontWeight] || fontWeight;
  }
}
